import { type TFunction } from 'i18next';
import { type ProductTileUnitRow as ProductTileUnitRowData } from 'src/lib/product-attributes-pim';
import type { OmnibusPriceDisplay } from '@/lib/omnibus';
import { OmnibusPriceIndication } from './omnibus-price-indication';
import { ProductTilePriceLine, ProductTileUnitRow, ProductTileVerticalBarInfo } from './tile-price-display';
import type { ProductItem } from '@/components/product-tile-custom/product-tile-custom-types';
import { Badge } from '../ui/badge';
export interface ProductTilePriceWeightProps {
    layout: 'horizontal' | 'vertical';
    productName: string;
    productId: string | undefined;
    t: TFunction<'product'>;
    isOnSale: boolean;
    currentFormatted: string;
    listFormatted?: string | null;
    unitRow: ProductTileUnitRowData;
    perUnitLine: string | null;
    pricePct: number;
    invertTextColor?: boolean;
    tileBasketLine: ProductItem | undefined;
    /** Omnibus price disclosure (`useProductPriceWeightDisplay().omnibus`); replaces the plain struck-through list price. */
    omnibus?: OmnibusPriceDisplay | null;
}

/**
 * Product tile price: current line + unit/weight, and vertical sale badge (when layout is vertical).
 */
export function ProductTilePriceWeight({
    layout,
    productName,
    productId,
    t,
    isOnSale,
    currentFormatted,
    listFormatted,
    unitRow,
    perUnitLine,
    pricePct,
    invertTextColor,
    tileBasketLine,
    omnibus,
}: ProductTilePriceWeightProps) {
    const isHorizontal = layout === 'horizontal';
    const a11y = productName || productId || '';

    const unitNode =
        unitRow.weightText || perUnitLine ? (
            <ProductTileUnitRow weightText={unitRow.weightText} perUnitText={perUnitLine ?? undefined} />
        ) : null;

    const showTilePreviousPrice = isHorizontal && Boolean(omnibus?.priorFormatted);

    const omnibusNode =
        omnibus && !showTilePreviousPrice ? (
            <OmnibusPriceIndication indication={omnibus} invertTextColor={invertTextColor} className="pt-1" />
        ) : null;

    const tilePreviousPrice = showTilePreviousPrice ? (
        <OmnibusPriceIndication indication={omnibus} invertTextColor={invertTextColor} />
    ) : null;

    const priceScreenReader = [
        a11y,
        listFormatted && !omnibus?.priorFormatted ? String(t('price.listPrice', { price: listFormatted })) : null,
        String(t('price.currentPrice', { price: currentFormatted })),
    ]
        .filter(Boolean)
        .join('. ');

    const priceLine = (
        <ProductTilePriceLine
            screenReaderLabel={priceScreenReader}
            currentFormatted={currentFormatted}
            listFormatted={showTilePreviousPrice ? null : listFormatted}
            isOnSale={isOnSale}
            invertTextColor={invertTextColor}
            pricePct={pricePct}
            tileBasketLine={tileBasketLine}
            omnibus={omnibusNode}
        />
    );

    if (isHorizontal) {
        if (unitNode) {
            return (
                <div className="flex w-full min-w-0 flex-col items-stretch gap-1">
                    {tilePreviousPrice}
                    {priceLine}
                    {unitNode}
                </div>
            );
        }
        return (
            <div className="flex w-full min-w-0 min-h-[44px] flex-col gap-1 pt-2.5">
                {tilePreviousPrice}
                {priceLine}
            </div>
        );
    }

    return (
        <ProductTileVerticalBarInfo
            priceBlock={priceLine}
            unitBlock={unitNode}
            invertTextColor={invertTextColor}
            endAdornment={
                isOnSale && pricePct > 0 ? (
                    <Badge hierarchy="primary" schema="danger" className="shrink-0">
                        -{pricePct}%
                    </Badge>
                ) : null
            }
        />
    );
}
